import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { initials } from "@/lib/format";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, BellRing, Users } from "lucide-react";

const BUCKET = {
  healthy: { labelKey: "mh.bucket_healthy", cls: "bg-emerald-100 text-emerald-700" },
  silent: { labelKey: "mh.bucket_silent", cls: "bg-amber-100 text-amber-700" },
  inactive: { labelKey: "mh.bucket_inactive", cls: "bg-orange-100 text-orange-700" },
  churned: { labelKey: "mh.bucket_churned", cls: "bg-rose-100 text-rose-700" },
};

export default function MembersHealth() {
  const t = useT();
  const { user } = useAuth();
  const { circleId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudgeTarget, setNudgeTarget] = useState(null);
  const [nudgeMsg, setNudgeMsg] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/circles/${circleId}/members-health`);
      setData(d);
    } catch (err) { toast.error(apiError(err, t("mh.load_err"))); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (!user || user.role !== "coach") return;
    load();
  }, [circleId, user]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const sendNudge = async () => {
    setBusy(true);
    try {
      await api.post(`/circles/${circleId}/nudge/${nudgeTarget.id}`, { message: nudgeMsg.trim() || null });
      toast.success(t("mh.nudge_sent"));
      setNudgeTarget(null); setNudgeMsg("");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const sendBulk = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/circles/${circleId}/nudge-inactive`, { message: bulkMsg.trim() || null });
      toast.success(t("mh.bulk_nudged", { n: r.nudged }));
      setBulkOpen(false); setBulkMsg("");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const buckets = data?.buckets || { healthy: 0, silent: 0, inactive: 0, churned: 0 };
  const members = data?.members || [];
  const silentCount = (buckets.silent || 0) + (buckets.inactive || 0);

  return (
    <div data-testid="members-dashboard">
      <Seo title={t("mh.seo")} noindex />
      <Link to="/app/coach/circles" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="members-back">
        <ChevronLeft className="h-4 w-4" /> {t("cir.title")}
      </Link>
      <PageHeader
        eyebrow={t("mh.eyebrow")}
        title={t("mh.title")}
        subtitle={t("mh.subtitle")}
        action={
          silentCount > 0 ? (
            <Button variant="outline" className="gap-1.5 rounded-full" onClick={() => setBulkOpen(true)} data-testid="nudge-all-silent">
              <BellRing className="h-4 w-4" /> {t("mh.all_silent", { n: silentCount })}
            </Button>
          ) : null
        }
      />

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : members.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Users className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("mh.empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("mh.empty_desc")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {members.map((m) => {
            const b = BUCKET[m.bucket] || BUCKET.healthy;
            return (
              <div key={m.id} data-testid={`member-card-${m.id}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
                <Avatar className="h-10 w-10">
                  {m.photo && <AvatarImage src={m.photo} alt={m.name} />}
                  <AvatarFallback>{initials(m.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-semibold text-foreground">{m.name}</p>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${b.cls}`}>{t(b.labelKey)}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {m.days_since_active == null ? t("mh.not_active") : t("mh.last_active", { n: m.days_since_active })}
                    {m.subscription?.expiring_soon ? t("mh.expiring") : ""}
                  </p>
                </div>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { setNudgeTarget(m); setNudgeMsg(""); }} data-testid={`nudge-member-${m.id}`}>
                  <BellRing className="h-3.5 w-3.5" /> {t("mh.nudge")}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Single nudge */}
      <Dialog open={!!nudgeTarget} onOpenChange={(o) => !o && setNudgeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("mh.nudge_title", { name: nudgeTarget?.name })}</DialogTitle>
            <DialogDescription>{t("mh.nudge_desc")}</DialogDescription>
          </DialogHeader>
          <Input value={nudgeMsg} onChange={(e) => setNudgeMsg(e.target.value)} placeholder={t("mh.nudge_ph")} data-testid="nudge-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNudgeTarget(null)} data-testid="nudge-cancel">{t("cir.cancel")}</Button>
            <Button onClick={sendNudge} disabled={busy} data-testid="nudge-submit">{t("mh.send")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk nudge */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("mh.bulk_title")}</DialogTitle>
            <DialogDescription>{t("mh.bulk_desc")}</DialogDescription>
          </DialogHeader>
          <Input value={bulkMsg} onChange={(e) => setBulkMsg(e.target.value)} placeholder={t("mh.nudge_ph")} data-testid="nudge-bulk-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-cancel">{t("cir.cancel")}</Button>
            <Button onClick={sendBulk} disabled={busy} data-testid="bulk-submit">{t("mh.send")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
